import { Box, Button, Card, CardContent, CardHeader, Divider, Stack, Typography } from "@mui/material";
import React, { FC, Fragment } from "react";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { intersectionChildSchema } from "@sinch/components/application/Rate/useIntervalType";
import { SelectInput } from "@sinch/components/form/SelectInput/SelectInput";
import { useIntersectionUnitField } from "@sinch/components/application/RateInterval/CreateInterval/useIntersectionUnitField";
import { UnitType } from "@sinch/core/entities/serverEnums";
import { IntervalTypeVariants } from "@sinch/components/application/RateInterval/IntervalFormDialog";
import { InputSkeleton } from "@sinch/components/form/InputSkeleton/InputSkeleton";

const MIN_UNITS = 2;

/**
 * Editor for a CustomIntervalType.INTERSECTION interval: a list of two or more units
 * combined with AND. The resulting interval keeps only the time covered by every unit.
 */
export const IntersectionSettings: FC<{ path: string; disabled?: boolean }> = ({ path, disabled }) => {
  const { t } = useTranslation();
  const unitsPath = `${path}.units`;
  const { fields, append, remove } = useFieldArray({ name: unitsPath });

  return (
    <Stack spacing={1.5}>
      <Typography color="text.secondary" variant="body2">
        {t("RateUnit.intersectionHelp")}
      </Typography>
      {fields.map((field, index) => (
        <Fragment key={field.id}>
          {index > 0 && (
            <Divider>
              <Typography variant="caption">{t("RateUnit.and")}</Typography>
            </Divider>
          )}
          <IntersectionChildRow
            disabled={disabled}
            index={index}
            onRemove={!disabled && fields.length > MIN_UNITS ? () => remove(index) : undefined}
            unitsPath={unitsPath}
          />
        </Fragment>
      ))}
      {!disabled && (
        <Button
          onClick={() => append(intersectionChildSchema.getDefault())}
          sx={{ alignSelf: "flex-start" }}
          variant="text"
        >
          {t("RateUnit.addUnit")}
        </Button>
      )}
    </Stack>
  );
};

const IntersectionChildRow: FC<{
  unitsPath: string;
  index: number;
  disabled?: boolean;
  onRemove?: () => void;
}> = ({ unitsPath, index, disabled, onRemove }) => {
  const { t } = useTranslation();
  const { unitTypeField, customTypeField, options, currentValue, handleChange, defaultCustomType, loading } =
    useIntersectionUnitField(unitsPath, index);

  return (
    <Card variant="outlined">
      <CardHeader
        action={
          onRemove && (
            <Button color="error" onClick={onRemove} size="small" variant="text">
              {t("RateUnit.removeUnit")}
            </Button>
          )
        }
        sx={{ pb: 0 }}
        title={t("RateUnit.unitNumber", { number: index + 1 })}
      />
      <CardContent sx={{ pt: 1 }}>
        <InputSkeleton active={loading} formControlProps={{ label: t("RateUnit.unitType"), required: true }}>
          <SelectInput
            {...unitTypeField}
            disabled={disabled}
            disablePortal
            fullWidth
            label={t("RateUnit.unitType")}
            onChange={handleChange}
            options={options}
            value={currentValue}
          />
        </InputSkeleton>
        {unitTypeField.value === UnitType.Custom && (
          <Box sx={{ mt: 1.5 }}>
            <IntervalTypeVariants
              disabled={disabled}
              path={`${unitsPath}.${index}.settings`}
              type={customTypeField.value ?? defaultCustomType}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
